import { useCallback, useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '../../api/client';
import type { AuditLogRow } from '../../api/types';
import { downloadCsv, rowsToCsv } from '../../utils/exportCsv';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function AuditSection() {
  const [logs, setLogs] = useState<AuditLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listAuditLogs(1, 100, actionFilter.trim() || undefined);
      setLogs(res.logs);
      setError(null);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [actionFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const exportCsv = () => {
    const headers = ['created_at', 'employee_email', 'action', 'target_id', 'ip_address', 'details'] as const;
    const rows = logs.map((l) => [
      l.created_at,
      l.employee_email,
      l.action,
      l.target_id ?? '',
      l.ip_address ?? '',
      JSON.stringify(l.details ?? {}),
    ]);
    downloadCsv(`audit-logs-${new Date().toISOString().slice(0, 10)}.csv`, rowsToCsv(headers, rows));
  };

  return (
    <>
      <div className="section-header console-header">
        <div>
          <h1 className="console-title">Audit log</h1>
          <p className="console-sub">Staff actions on merchants, fees, settlement wallets, payouts</p>
        </div>
        <div className="row-actions">
          <input
            className="login-input"
            style={{ maxWidth: 220 }}
            placeholder="Filter by action"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void load();
            }}
          />
          <button type="button" className="btn-outline btn-sm" onClick={() => void load()}>
            Apply
          </button>
          <button
            type="button"
            className="btn-outline btn-sm"
            disabled={logs.length === 0}
            onClick={exportCsv}
          >
            Export CSV
          </button>
        </div>
      </div>

      {error ? <p className="form-error section-error">{error}</p> : null}

      <div className="table-wrap">
        <div className="table-head data-table-head">
          <div className="th">Time</div>
          <div className="th">Staff</div>
          <div className="th">Action</div>
          <div className="th">Target</div>
          <div className="th">Details</div>
        </div>
        {loading ? (
          <div className="table-row">
            <div className="col-span-all">Loading…</div>
          </div>
        ) : logs.length === 0 ? (
          <div className="table-row">
            <div className="col-span-all">No audit events</div>
          </div>
        ) : (
          logs.map((log) => (
            <div key={log.id} className="table-row data-table-row">
              <div>{formatDate(log.created_at)}</div>
              <div>{log.employee_email}</div>
              <div className="cell-mono" style={{ fontSize: 11 }}>
                {log.action}
              </div>
              <div className="cell-mono">{log.target_id?.slice(0, 8) ?? '—'}</div>
              <div className="cell-muted" style={{ fontSize: 11 }}>
                {JSON.stringify(log.details).slice(0, 80)}
                {(JSON.stringify(log.details).length ?? 0) > 80 ? '…' : ''}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
